import React, { useEffect, useState } from 'react';
import { useAuth }            from './auth/useAuth';
import { useAppState }        from './hooks/useAppState';
import { useNavigation }      from './hooks/useNavigation';
import LoginScreen            from './components/LoginScreen';
import WelcomeScreen          from './components/WelcomeScreen';
import TopBar                 from './components/TopBar';
import Breadcrumb             from './components/Breadcrumb';
import GlobalProgressBar      from './components/GlobalProgressBar';
import Footer                 from './components/Footer';
import TreeFAB                from './components/TreeFAB';
import ProcessTree            from './components/ProcessTree';
import RoleScreen             from './components/RoleScreen';
import DashScreen             from './components/DashScreen';
import SectionScreen          from './components/SectionScreen';
import ChatPanel              from './components/ChatPanel';
import './index.css';

export default function App() {
  const auth = useAuth();
  const { state, setRole, setStep, wizSelect, wizBack, wizReset } = useAppState();
  const { page, currentSection, navigate, goBack, goTo, setCurrentSection } = useNavigation('welcome');
  const [showWelcome, setShowWelcome]   = useState(!state.role);
  const [showTree,    setShowTree]      = useState(false);
  const [treeOrigin,  setTreeOrigin]    = useState(null); // 'dash' | 'section' — where tree was opened from

  useEffect(() => {
    if (state.role) {
      setShowWelcome(false);
      if (page === 'welcome' || page === 'role') goTo('dash');
    }
    // eslint-disable-next-line
  }, []);

  if (!auth.authenticated) {
    return (
      <LoginScreen
        onLogin={auth.login} error={auth.error} loading={auth.loading}
        isLocked={auth.isLocked} lockSecsLeft={auth.lockSecsLeft} attempts={auth.attempts}
      />
    );
  }

  const handleWelcomeStart  = () => { setShowWelcome(false); goTo('role'); };
  const handleSelectRole    = (rk) => { setRole(rk); goTo('dash'); };
  const handleChangeRole    = () => goTo('role');
  const handleDash          = () => goTo('dash');

  const handleOpenSection = (sectionKey) => {
    setCurrentSection(sectionKey);
    navigate('section', sectionKey);
    // If opened from tree — keep tree open so highlight is visible
  };

  // Back from section: if tree is open, stay on same page but tree stays visible
  // If tree was origin, close section and return to wherever tree was
  const handleBack = () => {
    if (showTree && treeOrigin) {
      // Just close section, keep tree open
      setCurrentSection(null);
      if (treeOrigin === 'dash') goTo('dash');
    } else {
      goBack();
    }
  };

  const handleTreeToggle = () => {
    if (!showTree) {
      setTreeOrigin(page); // remember where we opened tree from
    } else {
      setTreeOrigin(null);
    }
    setShowTree(t => !t);
  };

  // When tree opens a section — remember origin as 'tree'
  const handleTreeSection = (sectionKey) => {
    setTreeOrigin('tree');
    setCurrentSection(sectionKey);
    navigate('section', sectionKey);
  };

  const handleChatSetRole = (roleKey) => {
    setRole(roleKey);
    if (page !== 'dash' && page !== 'section') goTo('dash');
  };
  const handleChatNavigate = (targetPage, sectionKey) => {
    if (targetPage === 'section' && sectionKey) handleOpenSection(sectionKey);
    else if (targetPage === 'role') handleChangeRole();
    else if (targetPage === 'dash') handleDash();
  };

  if (showWelcome) {
    return (
      <>
        <WelcomeScreen onStart={handleWelcomeStart} />
        <Footer />
        <ChatPanel state={state} currentSection={null}
          onNavigate={handleChatNavigate} onSetRole={handleChatSetRole} />
      </>
    );
  }

  return (
    <div className="app-layout">
      <TopBar
        state={state} page={page}
        onBack={handleBack}
        onChangeRole={handleChangeRole}
        onDash={handleDash}
        onLogout={auth.logout}
      />

      {state.role && <GlobalProgressBar state={state} />}

      <Breadcrumb
        page={page} currentSection={currentSection}
        onDash={handleDash} onRole={() => goTo('role')}
      />

      <div className="app-main">
        {page === 'role' && (
          <RoleScreen state={state} onSelectRole={handleSelectRole} onContinue={handleDash} />
        )}

        {page === 'dash' && state.role && (
          <>
            {showTree && (
              <div className="page-content" style={{paddingBottom:0}}>
                <ProcessTree
                  state={state}
                  onOpenSection={handleTreeSection}
                  selectedSection={currentSection}
                  onClose={() => setShowTree(false)}
                />
              </div>
            )}
            <DashScreen
              state={state} onOpenSection={handleOpenSection}
              onWizSelect={wizSelect} onWizBack={wizBack} onWizReset={wizReset}
              showTree={showTree} onToggleTree={handleTreeToggle}
            />
          </>
        )}

        {page === 'section' && currentSection && state.role && (
          <>
            {showTree && (
              <div className="page-content" style={{paddingBottom:0}}>
                <ProcessTree
                  state={state}
                  onOpenSection={handleTreeSection}
                  selectedSection={currentSection}
                  onClose={() => setShowTree(false)}
                />
              </div>
            )}
            <SectionScreen
              state={state} sectionKey={currentSection}
              onSetStep={setStep} onNavSection={handleOpenSection}
            />
          </>
        )}
      </div>

      <Footer />

      {/* Persistent Tree FAB */}
      <TreeFAB
        visible={!!state.role && page !== 'role' && page !== 'welcome'}
        active={showTree}
        onClick={handleTreeToggle}
      />

      <ChatPanel
        state={state}
        currentSection={page === 'section' ? currentSection : null}
        onNavigate={handleChatNavigate}
        onSetRole={handleChatSetRole}
      />
    </div>
  );
}
